import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routes from '../Constants/routes';
import bankruptcyTerminationActions from '../Actions/bankruptcyTerminationActions';
import JobStatusBadge from '../Components/BankruptcyTermination/JobStatusBadge';
import SummaryBar from '../Components/BankruptcyTermination/SummaryBar';
import CustomerRow from '../Components/BankruptcyTermination/CustomerRow';
import Pagination from '../Components/BankruptcyTermination/Pagination';
import formattingUtils from '../Utils/formattingUtils';
import dateUtils from '../Utils/dateUtils';
import type { BankruptcyCustomerResultDto, BankruptcyTerminationState } from './BankruptcyTermination.types';
import ExpandableTable from '../Components/Fundamentals/Tables/ExpandedableTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ET = ExpandableTable as any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = any;

interface RootState {
    BankruptcyTerminationReducer: BankruptcyTerminationState;
}

const CUSTOMERS_PAGE_SIZE = 50;

const BankruptcyTerminationDetailContainer = () => {
    const dispatch: AnyDispatch = useDispatch();
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const jobDetail = useSelector((state: RootState) => state.BankruptcyTerminationReducer.jobDetail);
    const customerResults = useSelector((state: RootState) => state.BankruptcyTerminationReducer.customerResults);
    const totalPages = useSelector((state: RootState) => state.BankruptcyTerminationReducer.customerResultsTotalPages);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (Boolean(jobId)) {
            const actions = bindActionCreators(bankruptcyTerminationActions, dispatch);
            actions.getJobDetail(Number(jobId), page, CUSTOMERS_PAGE_SIZE);
        }
    }, [dispatch, jobId, page]);

    const isCurrentJob = jobDetail?.jobId === Number(jobId);
    const pagedCustomers = customerResults ?? [];

    return (
        <div className="container-fluid">
            <div className="d-flex align-items-center flex-wrap mb-12 gap-16">
                <button
                    className="btn btn-default btn-sm"
                    onClick={() => {
                        Promise.resolve(navigate(routes.bankruptcyTermination)).catch(() => {});
                    }}
                >
                    <i className="vismaicon vismaicon-sm vismaicon-arrow-left-circle" />
                    {t('bankruptcytermination.backtolist')}
                </button>
                <h2 className="mb-0">{t('bankruptcytermination.jobtitle', { id: jobId })}</h2>
                {jobDetail && isCurrentJob && (
                    <div className="d-flex align-items-center gap-8">
                        <JobStatusBadge status={jobDetail.status} />
                        {jobDetail.startedAt && (
                            <span className="text-muted">
                                {dateUtils.formatDateAndTime(jobDetail.startedAt)}
                                {jobDetail.duration !== null && jobDetail.duration !== undefined && ` · ${formattingUtils.formatDuration(jobDetail.duration)}`}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {!jobDetail || !isCurrentJob || !customerResults ? (
                <div className="spinner spinner-default-green" />
            ) : (
                <>
                    {Boolean(jobDetail.errorMessage) && <div className="alert alert-danger mb-3">{jobDetail.errorMessage}</div>}

                    <SummaryBar summary={jobDetail.summary} />

                    {customerResults.length === 0 ? (
                        <p>{t('bankruptcytermination.nocustomers')}</p>
                    ) : (
                        <>
                            <ET className="overflow-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                                <thead>
                                    <tr>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.orgnumber')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.customername')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.customernumber')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.companystatuscode')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.companystatusname')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.companystatusdate')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.customerupdatestatus')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.subscriptions')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.processedat')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.status')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.hasunpaidinvoices')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.hasvbproducts')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.aocollaboration')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.emailsent')}</th>
                                        <th className="position-sticky top-0 bg-default z-index-3">{t('bankruptcytermination.manuallyhandled')}</th>
                                    </tr>
                                </thead>
                                <ET.Body>
                                    {pagedCustomers.map((customer: BankruptcyCustomerResultDto) => (
                                        <CustomerRow key={customer.id} customer={customer} />
                                    ))}
                                </ET.Body>
                            </ET>
                            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default BankruptcyTerminationDetailContainer;
